import { useEffect, useMemo } from "react";
import {
  BaseTable,
  LeafyGreenTable,
  RowSorting,
  SortingState,
  TablePlaceholder,
  onChangeHandler,
  useLeafyGreenTable,
} from "@evg-ui/lib/components/Table";
import { useQueryParam } from "@evg-ui/lib/hooks";
import { getLocalStorageBoolean } from "@evg-ui/lib/utils/localStorage";
import { useTaskAnalytics } from "analytics";
import { getColumnsTemplate } from "components/TasksTable/Columns";
import { taskReviewStyles } from "components/TasksTable/styles";
import { TaskTableInfo } from "components/TasksTable/types";
import { DISABLE_TASK_REVIEW } from "constants/cookies";
import { TableQueryParams } from "constants/queryParams";
import {
  SortDirection,
  TaskQuery,
  TaskSortCategory,
} from "gql/generated/types";
import { useTableSort } from "hooks";
import { parseSortString } from "utils/queryString";

const { getDefaultOptions: getDefaultSorting } = RowSorting;

interface Props {
  execution: number;
  executionTasksFull: NonNullable<TaskQuery["task"]>["executionTasksFull"];
  isPatch: boolean;
}

const ExecutionTasksTable: React.FC<Props> = ({
  execution,
  executionTasksFull,
  isPatch,
}) => {
  const { sendEvent } = useTaskAnalytics();
  const taskReviewEnabled = !getLocalStorageBoolean(DISABLE_TASK_REVIEW, false);
  const [sorts, setSorts] = useQueryParam(TableQueryParams.Sorts, "");
  // Apply default sort if no sorting method is defined.
  useEffect(() => {
    if (!sorts) {
      setSorts(defaultSortQueryParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uniqueExecutions = new Set([
    execution,
    // @ts-expect-error: FIXME. This comment was added by an automated script.
    ...executionTasksFull.map((t) => t.execution),
  ]);

  const initialSorting = getInitialSorting(sorts);

  const columns = useMemo(
    () =>
      getColumnsTemplate({
        isPatch,
        onClickTaskLink: () =>
          sendEvent({ name: "Clicked execution tasks table link" }),
        showTaskExecutionLabel: uniqueExecutions.size > 1,
      }),
    [isPatch, sendEvent, uniqueExecutions.size],
  );

  const tableSortHandler = useTableSort({
    sendAnalyticsEvents: (sorter: SortingState) =>
      sendEvent({
        name: "Sorted execution tasks table",
        "sort.by": sorter.map(({ id }) => id as TaskSortCategory),
      }),
  });

  const table: LeafyGreenTable<TaskTableInfo> =
    useLeafyGreenTable<TaskTableInfo>({
      columns,
      data: executionTasksFull ?? [],
      defaultColumn: {
        enableColumnFilter: false,
        enableMultiSort: true,
      },
      initialState: {
        columnVisibility: { reviewed: taskReviewEnabled },
        sorting: initialSorting,
      },
      isMultiSortEvent: () => true, // Override default requirement for shift-click to multisort.
      maxMultiSortColCount: 2,
      onSortingChange: onChangeHandler<SortingState>(
        // @ts-expect-error: FIXME. This comment was added by an automated script.
        (s) => getDefaultSorting(table).onSortingChange(s),
        tableSortHandler,
      ),
    });

  return (
    <BaseTable
      css={taskReviewEnabled && taskReviewStyles}
      data-testid="execution-tasks-table"
      data-testid-row="execution-tasks-table-row"
      emptyComponent={<TablePlaceholder message="No execution tasks found." />}
      shouldAlternateRowColor
      table={table}
    />
  );
};

const getInitialSorting = (sorts: string): SortingState => {
  const initialSorting = sorts
    ? parseSortString<
        "id",
        "direction",
        TaskSortCategory,
        {
          id: TaskSortCategory;
          direction: SortDirection;
        }
      >(sorts, {
        sortCategoryEnum: TaskSortCategory,
        sortByKey: "id",
        sortDirKey: "direction",
      }).map(({ direction, id }) => ({
        id,
        desc: direction === SortDirection.Desc,
      }))
    : [{ id: TaskSortCategory.Status, desc: false }];

  return initialSorting;
};

const defaultSortQueryParam = `${TaskSortCategory.Status}:${SortDirection.Asc}`;

export default ExecutionTasksTable;
